/* WeQuote Dev Board — the customer roadmap export (PRD §5.7, verify §61/§62).
 * Stakeholder-facing "Coming up / Recently released", filtered by a date-range
 * preset and each card's showOnRoadmap toggle; all internal detail hidden.
 * custRoadmapStandalone builds the self-contained HTML download. */
import type { Feature } from './types'
import { fmtDate, todayISO } from './dates'
import { custDisplayName, custDescOf } from './logic'
import { datedCard } from './schedule'
import { OV_MONTHS } from './overview'
import type { MutResult } from './mutations'

export type RxRange = 'all' | 'month' | 'year' | 'nextyear' | 'next3'

export function custMonthLabel(iso: string): string {
  const p = ('' + iso).split('-')
  if (p.length < 2) return ''
  return OV_MONTHS[+p[1] - 1] + ' ' + p[0]
}
export function rxInRange(mo: string, range: RxRange, now?: Date): boolean {
  if (range === 'all') return true
  const n = now || new Date()
  const cy = n.getFullYear()
  const cm = n.getMonth() + 1
  const y = +('' + mo).slice(0, 4)
  const m = +('' + mo).slice(5, 7)
  if (range === 'month') return y === cy && m === cm
  if (range === 'year') return y === cy
  if (range === 'nextyear') return y === cy + 1
  if (range === 'next3') { const idx = y * 12 + m; const cur = cy * 12 + cm; return idx >= cur && idx < cur + 3 }
  return true
}
export function custRoadmapData(features: Feature[], range: RxRange, released: boolean, showAll: boolean, now?: Date): { coming: Feature[]; released: Feature[] } {
  const inc = (f: Feature) => showAll || f.showOnRoadmap !== false
  const coming = features.filter((f) =>
    f.status !== 'live' && f.status !== 'archived' && f.status !== 'needsreview' &&
    datedCard(f) && !!f.estCompletion && rxInRange(('' + f.estCompletion).slice(0, 7), range, now) && inc(f))
  const rel = released
    ? features.filter((f) => f.status === 'live' && !!f.completedOn && rxInRange(('' + f.completedOn).slice(0, 7), range, now) && inc(f))
    : []
  return { coming, released: rel }
}
export function groupByMonth(list: Feature[], key: 'estCompletion' | 'completedOn'): { mo: string; items: Feature[] }[] {
  const m: Record<string, Feature[]> = {}
  list.forEach((f) => {
    const mo = ('' + f[key]).slice(0, 7)
    ;(m[mo] = m[mo] || []).push(f)
  })
  return Object.keys(m).sort().map((mo) => ({ mo, items: m[mo] }))
}
export function rxToggleShow(fs: Feature[], id: number): MutResult {
  return {
    features: fs.map((f) => (f.id === id ? { ...f, showOnRoadmap: f.showOnRoadmap === false } : f)),
    changedIds: [id],
  }
}

export const CUST_ROADMAP_CSS =
  ".rx-doc{max-width:800px;margin:0 auto;padding:44px 48px;font-family:'Atkinson Hyperlegible',Arial,sans-serif;color:#111827;line-height:1.5;}" +
  '.rx-head{border-bottom:3px solid #1a1a2e;padding-bottom:16px;margin-bottom:26px;}' +
  '.rx-brand{font-size:13px;font-weight:800;letter-spacing:.1em;color:#ff4655;text-transform:uppercase;}' +
  '.rx-h1{font-size:30px;font-weight:800;margin-top:6px;}' +
  '.rx-sub{font-size:13px;color:#6b7280;margin-top:6px;}' +
  '.rx-section{margin-bottom:32px;}' +
  '.rx-sec-h{font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#2563eb;margin-bottom:16px;}' +
  '.rx-month{margin-bottom:18px;page-break-inside:avoid;}' +
  '.rx-month-h{font-size:14px;font-weight:800;color:#1a1a2e;border-left:4px solid #2563eb;padding-left:10px;margin-bottom:8px;}' +
  '.rx-item{padding:9px 0 9px 24px;border-bottom:1px solid #f0f0f0;}' +
  '.rx-name{font-size:15px;font-weight:700;}' +
  '.rx-desc{font-size:13px;color:#4b5563;margin-top:3px;}' +
  '.rx-empty{color:#9ca3af;font-style:italic;padding:12px 0;}' +
  '.rx-foot{margin-top:34px;font-size:11px;color:#9ca3af;border-top:1px solid #eee;padding-top:12px;}'

function esc(s: string): string {
  return ('' + s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
/** Static (non-editable) roadmap document HTML — shared by preview + download. */
export function custRoadmapDocHtml(features: Feature[], range: RxRange, released: boolean, now?: Date): string {
  const d = custRoadmapData(features, range, released, false, now)
  const coming = groupByMonth(d.coming, 'estCompletion')
  const rel = groupByMonth(d.released, 'completedOn').reverse()
  const item = (f: Feature) =>
    '<div class="rx-item"><div class="rx-name">' + esc(custDisplayName(f)) + '</div>' +
    (custDescOf(f) ? '<div class="rx-desc">' + esc(custDescOf(f)) + '</div>' : '') + '</div>'
  const section = (title: string, groups: { mo: string; items: Feature[] }[]) => {
    if (!groups.length) return ''
    const blocks = groups.map((g) => '<div class="rx-month"><div class="rx-month-h">' + custMonthLabel(g.mo + '-01') + '</div>' + g.items.map(item).join('') + '</div>').join('')
    return '<div class="rx-section"><div class="rx-sec-h">' + title + '</div>' + blocks + '</div>'
  }
  let body = section('Coming up', coming) + section('Recently released', rel)
  if (!body) body = '<div class="rx-empty">Nothing to show for this range.</div>'
  return '<div class="rx-doc">' +
    '<div class="rx-head"><div class="rx-brand">WeQuote</div><div class="rx-h1">Product Roadmap</div><div class="rx-sub">What we’re building and when · updated ' + fmtDate(todayISO()) + '</div></div>' +
    body +
    '<div class="rx-foot">Timings are indicative and may change.</div>' +
    '</div>'
}
export function custRoadmapStandalone(features: Feature[], range: RxRange, released: boolean, now?: Date): string {
  return '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>WeQuote Product Roadmap</title>' +
    '<link href="https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap" rel="stylesheet">' +
    '<style>body{margin:0;background:#fff;}' + CUST_ROADMAP_CSS + '</style></head><body>' +
    custRoadmapDocHtml(features, range, released, now) + '</body></html>'
}
